// Types for better type safety
interface GridConfig {
  dataIndex: string;
  text?: string;
  width?: number;
  align?: string;
  format?: string;
  renderer?: string;
  sorter?: string;
  xtype?: string;
}

interface ReportShipData {
  [key: string]: any;
}

/**
 * Extracts column order from GRIDCFG configuration
 * @param gridCfg - Array of grid configuration objects
 * @returns Array of column names in the specified order
 */
function extractColumnOrder(gridCfg: GridConfig[]): string[] {
  return gridCfg.map((config) => config.dataIndex);
}

/**
 * Alternative approach: Create ordered object using Map
 * Maps preserve insertion order guaranteed by ES2015+
 * @param obj - Object to reorder
 * @param columnOrder - Array of property names in desired order
 * @returns Map with properties in correct order
 */
function createOrderedMap<T extends Record<string, any>>(
  obj: T,
  columnOrder: string[],
): Map<string, any> {
  const orderedMap = new Map();

  // Add properties in the specified order
  columnOrder.forEach((key) => {
    if (obj.hasOwnProperty(key)) {
      orderedMap.set(key, obj[key]);
    }
  });

  // Add remaining properties
  Object.keys(obj).forEach((key) => {
    if (!orderedMap.has(key)) {
      orderedMap.set(key, obj[key]);
    }
  });

  return orderedMap;
}

/**
 * Convert Map back to object while preserving order
 * @param orderedMap - Map with ordered entries
 * @returns Object with preserved property order
 */
function mapToOrderedObject<T extends Record<string, any>>(
  orderedMap: Map<string, any>,
): T {
  const obj = Object.create(null);
  orderedMap.forEach((value, key) => {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      writable: true,
      configurable: true,
    });
  });
  return obj as T;
}

/**
 * Reorders an array of ship data objects based on GRIDCFG column configuration
 * @param shipData - Array of ship data objects to reorder
 * @param gridCfg - Grid configuration array
 * @returns Array of ship data with columns reordered
 */
export function reorderShipDataColumns(
  shipData: ReportShipData[],
  gridCfg: GridConfig[],
): ReportShipData[] {
  if (!shipData || !Array.isArray(shipData) || shipData.length === 0) {
    return shipData;
  }

  if (!gridCfg || !Array.isArray(gridCfg) || gridCfg.length === 0) {
    return shipData;
  }

  const columnOrder = extractColumnOrder(gridCfg);

  // Use Map approach for guaranteed ordering
  return shipData.map((ship) => {
    const orderedMap = createOrderedMap(ship, columnOrder);
    return mapToOrderedObject(orderedMap);
  });
}
